import { MAX_ATTACHMENT_CONTENT_LENGTH } from "@repo/app-types/constants/shared";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { SmallTabs } from "@repo/ui/Navigation/smallTabs";
import { type FC, useMemo } from "react";
import { AttachmentDiffView, computeDiffStats } from "./attachmentDiffView.js";

interface AttachmentEditorContentProps {
  attachment:
    | {
        id: string;
        file_name: string;
        title: string | null;
        description: string | null;
        created_at: string;
        body: string | null;
        type: "text" | "markdown";
      }
    | undefined;
  isLoading: boolean;
  isSaving: boolean;
  editedBody: string;
  savedBody: string;
  setEditedBody: (value: string) => void;
  selectedTabIndex: number;
  onSelectedTabIndexChange: (index: number) => void;
}

export const AttachmentEditorContent: FC<AttachmentEditorContentProps> = ({
  attachment,
  isLoading,
  isSaving,
  editedBody,
  savedBody,
  setEditedBody,
  selectedTabIndex,
  onSelectedTabIndexChange,
}) => {
  const { added, removed } = useMemo(
    () => computeDiffStats(savedBody, editedBody),
    [savedBody, editedBody],
  );

  const tabs = useMemo(
    () => ["Edit", `Diff (+${added}/-${removed})`],
    [added, removed],
  );

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white">
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader style={LoaderStyle.SPINNER} />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col p-6">
          <h2 className="mb-4 text-base font-medium text-foreground">
            {attachment?.title ?? attachment?.file_name ?? ""}
          </h2>
          <div className="mb-4">
            <SmallTabs
              tabs={tabs}
              selectedTabIndex={selectedTabIndex}
              onSelectTabIndex={onSelectedTabIndexChange}
            />
          </div>
          {selectedTabIndex === 0 ? (
            <Textarea
              value={editedBody}
              onChange={setEditedBody}
              placeholder="Enter content..."
              isDisabled={isSaving}
              height="100%"
              maxLength={MAX_ATTACHMENT_CONTENT_LENGTH}
              className="min-h-0 flex-1 [&>div]:h-full"
            />
          ) : (
            <AttachmentDiffView savedBody={savedBody} editedBody={editedBody} />
          )}
        </div>
      )}
    </div>
  );
};
